import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid, Tooltip } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import DateRangeIcon from '@material-ui/icons/DateRange';
import { AsyncInputAutocomplete, Form, Input, Switch } from '@engloba-tech/englobity';
import { NAME_SPACE } from 'i18n';
import { tobaccoProducts } from 'services';
import { READER_REQUEST_TYPES, READER_REQUEST_BOX_READ_MODES, LocaleDatePicker, SelectInput, enumHelper } from 'shared';
import { useCreateRequestForm } from './useCreateRequestForm';
import { useFormCreateRequestStyles } from './formCreateRequest.styles';

export function FormCreateRequest({ formRef, onSubmit, errors }) {
  const { t } = useTranslation(NAME_SPACE.READER_REQUESTS);
  const { inputs, handleChange, handleTobaccoProductAutocomplete, handleChangeRequestType, handleChangeBoxReadMode } =
    useCreateRequestForm();
  const classes = useFormCreateRequestStyles();

  const boxReadModes = useMemo(() => {
    return enumHelper.enumToCombo(READER_REQUEST_BOX_READ_MODES, t, 'boxReadModes', false);
  }, [t]);

  async function handleSubmit() {
    onSubmit(inputs);
  }

  return (
    <Form className={classes.form} elementRef={formRef} onSubmit={handleSubmit} autoComplete="off" errors={errors}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={12}>
          <AsyncInputAutocomplete
            onChange={(e, element) => handleTobaccoProductAutocomplete(element)}
            label={t('properties.product')}
            required
            variant="outlined"
            icon={<SearchIcon />}
            value={inputs.tobaccoProductId ? { name: inputs.tobaccoProduct, id: inputs.tobaccoProductId } : null}
            defaultInputValue={inputs.tobaccoProduct || ''}
            getOptionSelected={(option, value) => option.id === value.id}
            getOptionLabel={option => option.name}
            requestAction={tobaccoProducts.getUsingQueryFilter}
            validators={['required']}
            errorMessages={[t('validations.required')]}
          />
        </Grid>
        <Grid item xs={12} md={12} className={classes.requestTypesContainer}>
          <Tooltip title={t('create.linkBoxRingTooltip')} placement={'bottom'}>
            <div className={classes.tooltipWrapper}>
              <Switch
                label={t('types.linkBoxRing')}
                checked={inputs.requestType ? READER_REQUEST_TYPES.linkBoxRing & inputs.requestType : false}
                onChange={() => handleChangeRequestType(READER_REQUEST_TYPES.linkBoxRing)}
                inputProps={{
                  validators: ['required'],
                  errorMessages: [t('validations.required')]
                }}
              />
            </div>
          </Tooltip>
          <div className={classes.inlineOptions}>
            <Tooltip title={t('create.activateTagsTooltip')} placement={'bottom'}>
              <div className={classes.tooltipWrapper}>
                <Switch
                  label={t('types.activateTags')}
                  checked={inputs.requestType ? READER_REQUEST_TYPES.activateTags & inputs.requestType : false}
                  onChange={() => handleChangeRequestType(READER_REQUEST_TYPES.activateTags)}
                />
              </div>
            </Tooltip>
            <Tooltip title={t('create.deactivateTagsTooltip')} placement={'bottom'}>
              <div className={classes.tooltipWrapper}>
                <Switch
                  label={t('types.deactivateTags')}
                  checked={inputs.requestType ? READER_REQUEST_TYPES.deactivateTags & inputs.requestType : false}
                  onChange={() => handleChangeRequestType(READER_REQUEST_TYPES.deactivateTags)}
                />
              </div>
            </Tooltip>
          </div>
          <Tooltip title={t('create.setProductionDateTooltip')} placement={'bottom'}>
            <div className={classes.tooltipWrapper}>
              <Switch
                label={t('types.setProductionDate')}
                checked={inputs.requestType ? READER_REQUEST_TYPES.setProductionDate & inputs.requestType : false}
                onChange={() => handleChangeRequestType(READER_REQUEST_TYPES.setProductionDate)}
              />
            </div>
          </Tooltip>
          {/* Comentado en feedback 9943
          <Tooltip title={t('create.replaceDefectiveTagsTooltip')} placement={'bottom'}>
            <div className={classes.tooltipWrapper}>
              <Switch
                label={t('types.replaceDefectiveTags')}
                checked={inputs.requestType ? READER_REQUEST_TYPES.replaceDefectiveTags & inputs.requestType : false}
                onChange={() => handleChangeRequestType(READER_REQUEST_TYPES.replaceDefectiveTags)}
              />
            </div>
          </Tooltip> */}
        </Grid>
        {(inputs.requestType & READER_REQUEST_TYPES.replaceDefectiveTags) <= 0 && (
          <>
            <Grid item xs={12} md={6}>
              <SelectInput
                name={t('create.properties.boxReadMode')}
                label={t('create.properties.boxReadMode')}
                elements={boxReadModes}
                required
                value={inputs.boxReadMode}
                onChange={handleChange('boxReadMode')}
                validators={['required']}
                errorMessages={[t('validations.required')]}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Input
                required
                name={t('create.properties.amountToRead')}
                label={t('create.properties.amountToRead')}
                variant="outlined"
                type="number"
                value={inputs.amountToRead || ''}
                onChange={handleChange('amountToRead')}
                inputProps={{ 'aria-label': t('create.properties.amountToRead') }}
                validators={['required', 'minNumber:1', 'matchRegexp:^[0-9]+$']}
                errorMessages={[
                  t('validations.required'),
                  t('validations.minNumber', { minimum: 1 }),
                  t('validations.noDecimal')
                ]}
              />
            </Grid>
          </>
        )}
        {(inputs.requestType & READER_REQUEST_TYPES.replaceDefectiveTags) > 0 && (
          <>
            <Grid item xs={12} md={6}>
              <Input
                required
                name={t('create.properties.ringAmount')}
                label={t('create.properties.ringAmount')}
                variant="outlined"
                type="number"
                value={inputs.ringAmount || ''}
                onChange={handleChange('ringAmount')}
                inputProps={{ 'aria-label': t('create.properties.ringAmount') }}
                validators={['required', 'minNumber:1', 'matchRegexp:^[0-9]+$']}
                errorMessages={[
                  t('validations.required'),
                  t('validations.minNumber', { minimum: 1 }),
                  t('validations.noDecimal')
                ]}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Input
                multiline
                required
                rows={2}
                name={t('create.properties.observations')}
                label={t('create.properties.observations')}
                variant="outlined"
                value={inputs.observations || ''}
                onChange={handleChange('observations')}
                inputProps={{ 'aria-label': t('create.properties.observations') }}
                validators={['required', 'maxStringLength:500']}
                errorMessages={[t('validations.required'), t('common:validations.maxStringLength', { length: 500 })]}
                onKeyPress={e => {
                  if (e.which === 13) {
                    e.stopPropagation();
                  }
                }}
              />
            </Grid>
          </>
        )}
        {(inputs.requestType & READER_REQUEST_TYPES.setProductionDate) > 0 && (
          <Grid item xs={12} md={6}>
            <LocaleDatePicker
              required
              icon={<DateRangeIcon />}
              name={t('create.properties.productionDate')}
              label={t('create.properties.productionDate')}
              value={inputs.productionDate}
              onChange={handleChange('productionDate')}
              inputProps={{ 'aria-label': 'productionDate' }}
              validators={['required']}
              errorMessages={[t('validations.required')]}
            />
          </Grid>
        )}
      </Grid>
    </Form>
  );
}

FormCreateRequest.propTypes = {
  formRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  onSubmit: PropTypes.func.isRequired,
  errors: PropTypes.object
};
